import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ButtonBase, Collapse, type CollapseProps, Typography } from "@mui/material";

type ShowMoreCollapseProps = Omit<CollapseProps, "in"> & {
  isExpanded: boolean;
  onExpand: () => void;
};

/**
 * Drop-in replacement for MUI Collapse in fulfillment panels that have always-visible
 * summary content above collapsible details. Shows a "Show More" affordance when collapsed
 * so users can tell more content exists below.
 */
export function ShowMoreCollapse({
  isExpanded,
  onExpand,
  children,
  sx,
  ...collapseProps
}: ShowMoreCollapseProps) {
  return (
    <>
      {!isExpanded && (
        <ButtonBase
          onClick={onExpand}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            color: "text.disabled",
            pt: 1,
            width: "100%",
            "&:hover": { color: "text.secondary" },
          }}
        >
          <Typography variant="caption">Show More</Typography>
          <ExpandMoreIcon fontSize="small" />
        </ButtonBase>
      )}
      <Collapse
        in={isExpanded}
        // MUI Collapse adds a top margin via its transition styles even when
        // collapsed; override it so the "Show More" button sits flush.
        sx={{ mt: isExpanded ? undefined : "0 !important", ...sx }}
        {...collapseProps}
      >
        {children}
      </Collapse>
    </>
  );
}
